import { useEffect } from 'react';
import { watchMediaRecovery } from '@aquilixshop/ui-react';

/**
 * LE FILET DES IMAGES, DANS L'ÉDITEUR (fondatrice, 18/09/2026 : « c'est la deuxième fois que ça
 * arrive, on ne charge pas les images dans le builder… le problème c'est que c'était déjà chargé »).
 *
 * ── CE QUI SE PASSE ──────────────────────────────────────────────────────────────────────────────────
 * Chaque visuel du canvas est un aller-retour vers l'espace de stockage traversé par la JVM, sans CDN ni
 * cache devant, et une page en demande une dizaine d'un coup. Une seule requête qui trébuche — coupure,
 * 503 passager, lenteur — laisse la boîte morte : un navigateur ne redemande JAMAIS de lui-même une
 * image qu'il a ratée. D'où le symptôme exact qu'elle décrit : ça avait chargé, et ça ne charge plus.
 *
 * ── POURQUOI ÇA A DÛ ARRIVER DEUX FOIS ───────────────────────────────────────────────────────────────
 * L'échelle de rattrapage existe depuis le 17/09… mais elle n'a été branchée QUE sur la vitrine
 * (`Layout.astro`) et dans UN bloc React (`ResilientImg`, la galerie produit). Le canvas du builder n'est
 * pas un iframe de la vitrine : c'est le même arbre React rendu ici, avec 114 `<img>` nus qui n'ont
 * jamais eu une ligne de secours. Le correctif couvrait la boutique et laissait l'atelier dehors.
 *
 * ── POURQUOI UN VEILLEUR ET PAS UN APPEL ─────────────────────────────────────────────────────────────
 * La vitrine peut se contenter d'un balayage : son HTML arrive d'un bloc et ne bouge plus. Ici, les
 * sections se remontent à chaque réglage, chaque glisser-déposer, chaque changement de page — les images
 * nées après le balayage seraient sans filet. `watchMediaRecovery` re-balaie quand le DOM bouge.
 *
 * On veille sur les DEUX hôtes du canvas (l'édition et l'aperçu propre) : la panne ne demande pas si on
 * est en train d'éditer.
 *
 * ── POURQUOI LE NŒUD, ET PAS UN `ref` (19/09/2026, 3e signalement) ───────────────────────────────────
 * « J'ai activé et désactivé le preview […] et les images ne s'affichent plus. » Basculer l'aperçu ne
 * démonte pas `BuilderCanvas` : il rend un autre arbre. Le div d'édition part et revient — un AUTRE
 * nœud — mais un `RefObject` est stable, donc l'effet ne se rejouait jamais : le veilleur restait collé
 * à l'ancien div, détaché du document. Le canvas d'édition repartait SANS filet pour le reste de la
 * session, et la première image qui trébuche y restait morte. On prend donc l'élément lui-même : le
 * remontage devient une dépendance ordinaire (et le ménage de l'ancien veilleur part enfin).
 */
export function useCanvasMediaRecovery(root: HTMLElement | null): void {
  useEffect(() => {
    if (!root) return;
    return watchMediaRecovery(root);
  }, [root]);
}
